import { useState, useEffect, useCallback } from 'react';
import { getAdminUsers } from '../../api/admin';
import s from './AdminPages.module.css';

export default function AdminUsers({ onViewUser }) {
  const [data, setData] = useState({ users: [], pagination: {} });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');

  const fetchUsers = useCallback(() => {
    setLoading(true);
    getAdminUsers({ page, sort, order, search })
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, sort, order, search]);

  useEffect(() => { void (async () => { await fetchUsers(); })(); }, [fetchUsers]);

  function handleSort(col) {
    if (sort === col) {
      setOrder(order === 'desc' ? 'asc' : 'desc');
    } else {
      setSort(col);
      setOrder('desc');
    }
    setPage(1);
  }

  function sortIndicator(col) {
    if (sort !== col) return '';
    return order === 'asc' ? ' \u25B2' : ' \u25BC';
  }

  function handleSearch(e) {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  }

  const { pagination } = data;

  return (
    <div>
      <h1 className={s.title}>Users</h1>

      <form onSubmit={handleSearch} className={s.searchRow}>
        <input
          type="text"
          placeholder="Search by email or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={s.searchInput}
        />
        <button type="submit" className={s.searchBtn}>Search</button>
      </form>

      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th className={s.th} onClick={() => handleSort('email')}>
                Email{sortIndicator('email')}
              </th>
              <th className={s.th} onClick={() => handleSort('name')}>
                Name{sortIndicator('name')}
              </th>
              <th className={s.th}>Plan</th>
              <th className={s.th}>Children</th>
              <th className={s.th} onClick={() => handleSort('created_at')}>
                Created{sortIndicator('created_at')}
              </th>
              <th className={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className={s.td}>Loading...</td></tr>
            ) : data.users.length === 0 ? (
              <tr><td colSpan={6} className={s.td}>No users found</td></tr>
            ) : (
              data.users.map((user) => (
                <tr key={user.id} className={s.row}>
                  <td className={s.td}>{user.email}</td>
                  <td className={s.td}>{user.name || '-'}</td>
                  <td className={s.td}>
                    <PlanBadge type={user.plan_type} trialEnds={user.trial_ends_at} />
                  </td>
                  <td className={s.td}>{user.children_count}</td>
                  <td className={s.td}>{formatDate(user.created_at)}</td>
                  <td className={s.td}>
                    <button onClick={() => onViewUser(user.id)} className={s.viewBtn}>
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className={s.paginationRow}>
          <button
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            className={s.pageBtn}
          >
            Prev
          </button>
          <span className={s.pageInfo}>
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            disabled={page >= pagination.pages}
            onClick={() => setPage(page + 1)}
            className={s.pageBtn}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function PlanBadge({ type, trialEnds }) {
  const variantMap = {
    trial: s.badgeTrial,
    monthly: s.badgeMonthly,
    annual: s.badgeAnnual,
    cancelled: s.badgeCancelled,
  };

  const variantClass = variantMap[type] || s.badgeDefault;

  let label = type || 'none';
  if (type === 'trial' && trialEnds) {
    const daysLeft = Math.ceil((new Date(trialEnds) - new Date()) / (1000 * 60 * 60 * 24));
    label = daysLeft > 0 ? `trial (${daysLeft}d)` : 'trial (expired)';
  }

  return <span className={`${s.badge} ${variantClass}`}>{label}</span>;
}

function formatDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
